import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, Grid, Paper, Stack, Typography
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import RestoreIcon from '@mui/icons-material/Restore';
import { api, useAuth } from '../services/AuthService';
import BrowserPrintService from '../services/BrowserPrintService';
import { toast } from 'react-toastify';

const ACTIVE_STATUSES = new Set(['pendente', 'pending', 'aceito', 'accepted', 'preparando', 'preparing']);

function normalizedStatus(value) {
  return String(value || '').trim().toLowerCase();
}

function parseCustomizations(value) {
  if (Array.isArray(value)) return value;
  try { return JSON.parse(value || '[]'); } catch (_) { return []; }
}

function formatTime(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--:--';
  return date.toLocaleTimeString('pt-BR', {
    hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo'
  });
}

function elapsedMinutes(value, now) {
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return 0;
  return Math.max(0, Math.floor((now - timestamp) / 60000));
}

function itemOptionText(item) {
  return parseCustomizations(item.customizations).flatMap((group) => {
    const options = group.selected_options || group.selectedOptions || [];
    const names = options.map((option) => option.name).filter(Boolean);
    return names.length ? [`${group.group_name || group.groupName || 'Opcoes'}: ${names.join(', ')}`] : [];
  });
}

function hiddenOrdersStorageKey(lanchoneteId) {
  return `@AllDelivery:kitchen:hidden:${lanchoneteId || 'unknown'}`;
}

function loadHiddenOrderIds(lanchoneteId) {
  try {
    const stored = JSON.parse(localStorage.getItem(hiddenOrdersStorageKey(lanchoneteId)) || '[]');
    return new Set((Array.isArray(stored) ? stored : []).map(Number).filter(Number.isInteger));
  } catch (_) {
    return new Set();
  }
}

export default function KitchenQueue() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [mesasById, setMesasById] = useState({});
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });
  const [now, setNow] = useState(Date.now());
  const [hiddenOrderIds, setHiddenOrderIds] = useState(() => loadHiddenOrderIds(user?.lanchonete_id));
  const orderDetailsCache = useRef(new Map());

  useEffect(() => {
    setHiddenOrderIds(loadHiddenOrderIds(user?.lanchonete_id));
  }, [user?.lanchonete_id]);

  const loadQueue = useCallback(async ({ quiet = false } = {}) => {
    if (!user?.lanchonete_id) return;
    if (!quiet) setLoading(true);
    try {
      const [ordersResponse, mesasResponse] = await Promise.all([
        api.get('/pedidos'),
        api.get('/mesas').catch(() => ({ data: [] }))
      ]);
      const active = (ordersResponse.data || [])
        .filter((order) => ACTIVE_STATUSES.has(normalizedStatus(order.status)))
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      // Os detalhes (principalmente itens) mudam muito menos que o status. Antes,
      // a tela consultava novamente cada pedido a cada 10 segundos: com 86
      // pedidos eram 87 requisicoes por ciclo. O cache consulta apenas pedidos
      // novos ou realmente alterados.
      const activeIds = new Set(active.map((order) => Number(order.id)));
      for (const cachedId of orderDetailsCache.current.keys()) {
        if (!activeIds.has(cachedId)) orderDetailsCache.current.delete(cachedId);
      }
      const detailed = await Promise.all(active.map(async (order) => {
        const id = Number(order.id);
        const signature = String(order.updated_at || order.created_at || '');
        const cached = orderDetailsCache.current.get(id);
        if (cached && cached.signature === signature) {
          return { ...cached.order, ...order, items: cached.order.items || cached.order.itens || [] };
        }
        try {
          const response = await api.get(`/pedidos/${order.id}`);
          orderDetailsCache.current.set(id, { signature, order: response.data });
          return response.data;
        } catch (_) {
          if (cached) return { ...cached.order, ...order, items: cached.order.items || cached.order.itens || [] };
          return { ...order, items: [] };
        }
      }));
      const withKitchenItems = detailed.map((order) => ({
        ...order,
        items: (order.items || order.itens || []).filter((item) => BrowserPrintService.isKitchenItem(item))
      })).filter((order) => order.items.length > 0);
      setOrders(withKitchenItems);
      setMesasById((mesasResponse.data || []).reduce((map, mesa) => ({ ...map, [mesa.id]: mesa }), {}));
    } catch (error) {
      toast.error(error.response?.data?.error || 'Nao foi possivel carregar a fila da cozinha');
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [user?.lanchonete_id]);

  useEffect(() => {
    loadQueue();
    const refresh = window.setInterval(() => loadQueue({ quiet: true }), 10000);
    const clock = window.setInterval(() => setNow(Date.now()), 30000);
    return () => {
      window.clearInterval(refresh);
      window.clearInterval(clock);
    };
  }, [loadQueue]);

  const queue = useMemo(() => {
    let position = 0;
    return orders.filter((order) => !hiddenOrderIds.has(Number(order.id))).map((order) => ({
      ...order,
      items: order.items.map((item) => ({ ...item, queuePosition: ++position }))
    }));
  }, [orders, hiddenOrderIds]);

  const hiddenActiveCount = useMemo(
    () => orders.filter((order) => hiddenOrderIds.has(Number(order.id))).length,
    [orders, hiddenOrderIds]
  );

  const hideOrder = (orderId) => {
    setHiddenOrderIds((current) => {
      const next = new Set(current);
      next.add(Number(orderId));
      localStorage.setItem(hiddenOrdersStorageKey(user?.lanchonete_id), JSON.stringify([...next]));
      return next;
    });
    toast.info('Pedido retirado somente da tela da cozinha. Ele continua salvo no sistema.');
  };

  const restoreHiddenOrders = () => {
    localStorage.removeItem(hiddenOrdersStorageKey(user?.lanchonete_id));
    setHiddenOrderIds(new Set());
    toast.success('Pedidos ocultados voltaram para a fila da cozinha.');
  };

  const changeStatus = async (orderId, status) => {
    try {
      setUpdatingId(orderId);
      await api.patch(`/pedidos/${orderId}/status`, { status });
      toast.success(status === 'pronto' ? 'Pedido marcado como pronto' : 'Preparo iniciado');
      await loadQueue({ quiet: true });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Nao foi possivel atualizar o pedido');
    } finally {
      setUpdatingId(null);
    }
  };

  const markAllReady = async () => {
    if (bulkUpdating || queue.length === 0) return;
    const confirmed = window.confirm(
      `Deseja marcar os ${queue.length} pedidos visiveis como prontos?\n\n` +
      'Os clientes e motoboys poderao receber os avisos correspondentes.'
    );
    if (!confirmed) return;

    setBulkUpdating(true);
    setBulkProgress({ done: 0, total: queue.length });
    let completed = 0;
    const failures = [];

    // Lotes pequenos evitam sobrecarregar o servidor em dias de muito movimento.
    for (let index = 0; index < queue.length; index += 4) {
      const batch = queue.slice(index, index + 4);
      const results = await Promise.allSettled(
        batch.map((order) => api.patch(`/pedidos/${order.id}/status`, { status: 'pronto' }))
      );
      const successfulInBatch = results.filter((result) => result.status === 'fulfilled').length;
      completed += successfulInBatch;
      results.forEach((result, resultIndex) => {
        if (result.status === 'rejected') failures.push(batch[resultIndex].id);
      });
      setBulkProgress({ done: Math.min(index + batch.length, queue.length), total: queue.length });
    }

    if (completed) toast.success(`${completed} pedido(s) marcado(s) como pronto(s).`);
    if (failures.length) toast.error(`${failures.length} pedido(s) nao foram atualizados. Eles continuarao na fila.`);
    await loadQueue({ quiet: true });
    setBulkUpdating(false);
    setBulkProgress({ done: 0, total: 0 });
  };

  const totalItems = queue.reduce((total, order) => total + order.items.length, 0);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#f8fafc', minHeight: '100vh' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={2} mb={3}>
        <Box>
          <Stack direction="row" spacing={1.2} alignItems="center">
            <RestaurantIcon color="primary" sx={{ fontSize: 34 }} />
            <Typography variant="h4" fontWeight={900}>Fila da Cozinha</Typography>
          </Stack>
          <Typography color="text.secondary" mt={0.5}>Mais antigos primeiro. Atualizacao automatica a cada 10 segundos.</Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button
            variant="contained"
            color="success"
            startIcon={bulkUpdating ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
            onClick={markAllReady}
            disabled={loading || bulkUpdating || queue.length === 0}
          >
            {bulkUpdating ? `Atualizando ${bulkProgress.done}/${bulkProgress.total}` : `Marcar todos prontos (${queue.length})`}
          </Button>
          {hiddenActiveCount > 0 && (
            <Button variant="outlined" color="warning" startIcon={<RestoreIcon />} onClick={restoreHiddenOrders}>
              Mostrar {hiddenActiveCount} retirado{hiddenActiveCount === 1 ? '' : 's'}
            </Button>
          )}
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => loadQueue()} disabled={loading}>Atualizar agora</Button>
        </Stack>
      </Stack>

      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={3}><Paper sx={{ p: 2.2, borderRadius: 3 }}><Typography color="text.secondary">Pedidos na fila</Typography><Typography variant="h4" fontWeight={900}>{queue.length}</Typography></Paper></Grid>
        <Grid item xs={6} sm={3}><Paper sx={{ p: 2.2, borderRadius: 3 }}><Typography color="text.secondary">Itens para preparar</Typography><Typography variant="h4" fontWeight={900}>{totalItems}</Typography></Paper></Grid>
      </Grid>

      {loading ? <Box display="grid" sx={{ placeItems: 'center', py: 10 }}><CircularProgress /></Box> : queue.length === 0 ? (
        <Alert severity="success" icon={<CheckCircleIcon />}>Nao ha lanches aguardando preparo.</Alert>
      ) : (
        <Stack spacing={2}>
          {queue.map((order, orderIndex) => {
            const mesa = mesasById[order.mesa_id];
            const status = normalizedStatus(order.status);
            const minutes = elapsedMinutes(order.created_at, now);
            return (
              <Paper key={order.id} sx={{ borderRadius: 3, overflow: 'hidden', border: orderIndex === 0 ? '2px solid #ef4444' : '1px solid #e2e8f0' }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5} sx={{ px: 2.5, py: 2, bgcolor: orderIndex === 0 ? '#fff1f2' : '#fff' }}>
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Chip label={orderIndex === 0 ? 'PROXIMO' : `ORDEM ${orderIndex + 1}`} color={orderIndex === 0 ? 'error' : 'default'} sx={{ fontWeight: 900 }} />
                      <Typography variant="h6" fontWeight={900}>Pedido #{order.lanchonete_order_number || order.id}</Typography>
                      {mesa && <Chip label={`Mesa ${mesa.numero}`} color="primary" variant="outlined" />}
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.3, sm: 2 }} mt={1}>
                      <Typography fontWeight={800}>
                        <strong>Mesa:</strong> {mesa?.numero || (order.mesa_id ? order.mesa_id : 'Livre')}
                      </Typography>
                      <Typography fontWeight={800}>
                        <strong>Cliente:</strong> {order.customer_name || mesa?.customer_name || 'Nao informado'}
                      </Typography>
                    </Stack>
                  </Box>
                  <Stack alignItems={{ xs: 'flex-start', sm: 'flex-end' }} spacing={0.6}>
                    <Stack direction="row" spacing={0.7} alignItems="center"><AccessTimeIcon fontSize="small" /><Typography fontWeight={900}>{formatTime(order.created_at)}</Typography></Stack>
                    <Typography color={minutes >= 20 ? 'error' : 'text.secondary'} fontWeight={800}>Aguardando ha {minutes} min</Typography>
                  </Stack>
                </Stack>
                <Divider />
                <Box sx={{ p: 2.5 }}>
                  <Stack spacing={1.5}>
                    {order.items.map((item) => (
                      <Box key={`${order.id}-${item.id || item.produto_id}-${item.queuePosition}`} sx={{ p: 2, borderRadius: 2.5, bgcolor: '#f8fafc', borderLeft: '5px solid #e50914' }}>
                        <Typography variant="h6" fontWeight={950}>Lanche {item.queuePosition}: {item.quantity}x {item.produto_name || item.product_name || `Produto #${item.produto_id}`}</Typography>
                        {itemOptionText(item).map((text) => <Typography key={text} color="text.secondary" mt={0.5}>{text}</Typography>)}
                        {item.observation && <Alert severity="warning" sx={{ mt: 1 }}><strong>Observacao:</strong> {item.observation}</Alert>}
                      </Box>
                    ))}
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="flex-end" spacing={1.2} mt={2}>
                    <Button
                      variant="outlined"
                      color="inherit"
                      startIcon={<VisibilityOffIcon />}
                      disabled={bulkUpdating || updatingId === order.id}
                      onClick={() => hideOrder(order.id)}
                    >
                      Tirar da tela
                    </Button>
                    {!['preparando', 'preparing'].includes(status) && <Button variant="outlined" disabled={bulkUpdating || updatingId === order.id} onClick={() => changeStatus(order.id, 'preparando')}>Iniciar preparo</Button>}
                    <Button variant="contained" disabled={bulkUpdating || updatingId === order.id} onClick={() => changeStatus(order.id, 'pronto')}>Marcar pronto</Button>
                  </Stack>
                </Box>
              </Paper>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}
